import { STRIPE_PRODUCT_IDS } from './stripe-constants';
import type { SupportedRepubName } from './types';

export type SubscriptionProductConfig = {
  externalUrl?: Partial<Record<'PROD' | 'NON_PROD', string>>;
  isDefaultWhenSingleOption: boolean;
  name: 'WhoScored+' | 'Premium' | 'Essential';
  term: 'annually' | 'monthly';
};

export type SubscriptionConfig = {
  essentialFeatures?: { description?: string; isDisabled?: boolean; title: string }[];
  premiumFeatures: { description?: string; title: string }[];
  products: Record<string, SubscriptionProductConfig>;
};

export const SUBSCRIPTION_CONFIGS = {
  WS: {
    premiumFeatures: [
      { title: 'AI-powered projections' },
      { title: 'AI-powered probabilities' },
      { title: 'Easy Visualised Trends' },
    ],
    products: {
      [STRIPE_PRODUCT_IDS.WHOSCORED_PLUS_MONTHLY]: {
        name: 'WhoScored+',
        term: 'monthly',
        isDefaultWhenSingleOption: true,
      },
    },
  },
  US: {
    premiumFeatures: [
      { title: 'Positive Expected Value Bets' },
      { title: 'AI Probabilities for each bet' },
      { title: 'Public Betting Splits' },
      { title: 'Trends and insights for Each Bet' },
      { title: 'Custom Betslip' },
    ],
    products: {
      [STRIPE_PRODUCT_IDS.PREMIUM_ANNUALLY]: { name: 'Premium', term: 'annually', isDefaultWhenSingleOption: false },
      [STRIPE_PRODUCT_IDS.PREMIUM_MONTHLY]: { name: 'Premium', term: 'monthly', isDefaultWhenSingleOption: true },
    },
  },
  OC: {
    essentialFeatures: [
      {
        title: 'Unlimited Grids',
        description: 'Unlock unlimited comparison grids for over 500 sporting events daily',
      },
      {
        title: 'Market Movers',
        description: 'Unlock unlimited access to the top steamers and drifters daily',
      },
      {
        title: 'Reduced Ads',
        description: 'Never see banner advertising or popups again',
      },
      {
        title: 'Price History',
        description: 'Get unlimited access to price history across 25+ bookmakers',
      },
      {
        title: 'Free Racehorse Share',
        description: 'Claim an exclusive FREE racehorse share worth £40!',
      },
      {
        title: 'Premium Content',
        isDisabled: true,
      },
      {
        title: '60% off RacingTV',
        isDisabled: true,
      },
      {
        title: 'Value Bets',
        isDisabled: true,
      },
      {
        title: 'Trends',
        isDisabled: true,
      },
      {
        title: 'Public Betting Splits',
        isDisabled: true,
      },
    ],
    premiumFeatures: [
      {
        title: 'Unlimited Grids',
        description: 'Unlock unlimited comparison grids for over 500 sporting events daily',
      },
      {
        title: 'AI Probabilities on Grids',
        description: 'Visibility of our cutting edge AI probabilities on supported sporting events and markets',
      },
      {
        title: 'Market Movers',
        description: 'Unlock unlimited access to the top steamers and drifters daily',
      },
      {
        title: 'Reduced Ads',
        description: 'Never see banner advertising or popups again',
      },
      {
        title: 'Price History',
        description: 'Get unlimited access to price history across 25+ bookmakers',
      },
      {
        title: 'Free Racehorse Share',
        description: 'Claim an exclusive FREE racehorse share worth £40!',
      },
      {
        title: 'Premium Content',
        description: 'Unlock exclusive access to Andy Holding, Ginger Joe, Liam Firkin & more',
      },
      {
        title: '60% off RacingTV',
        description:
          'Get an exclusive 60% off RacingTV subscription at £11.99 per month for 12 months. Available to new and returning RacingTV customers',
      },
      {
        title: 'Value Bets',
        description: 'Get access to our Value Bets tool powered by cutting edge AI',
      },
      {
        title: 'Trends',
        description: 'Identify the best betting opportunities with our AI powered Trends',
      },
      {
        title: 'Public Betting Splits',
        description: 'Get access to where the smart money is going',
      },
    ],
    products: {
      [STRIPE_PRODUCT_IDS.ESSENTIAL_MONTHLY]: {
        name: 'Essential',
        term: 'monthly',
        isDefaultWhenSingleOption: false,
      },
      [STRIPE_PRODUCT_IDS.ESSENTIAL_ANNUALLY]: {
        name: 'Essential',
        term: 'annually',
        isDefaultWhenSingleOption: false,
      },
      [STRIPE_PRODUCT_IDS.PREMIUM_ANNUALLY]: { name: 'Premium', term: 'annually', isDefaultWhenSingleOption: false },
      [STRIPE_PRODUCT_IDS.PREMIUM_MONTHLY]: { name: 'Premium', term: 'monthly', isDefaultWhenSingleOption: true },
    },
  },
  GZ: {
    premiumFeatures: [
      { title: '+100 pronostici e quote con EV positivo al giorno' },
      { title: 'Calcolo della Probabilità AI per ogni scommessa' },
      { title: 'Tutti gli operatori comparati in tempo reale' },
      { title: 'Studi e approfondimenti per ogni scommessa' },
    ],
    products: {
      [STRIPE_PRODUCT_IDS.GAZZETTA_PLUS_MONTHLY]: {
        externalUrl: {
          NON_PROD:
            'https://dev.abbonamenti.gazzetta.it/login/?OfferId=OFF-00136830_1&returnUrl=https://dv1-fpsm.gazzetta.it&pwcmp=AIPREDMensile',
          PROD: 'https://abbonamenti.gazzetta.it/login/?OfferId=OFF-00136830_1&returnUrl=https://aipredictor.gazzetta.it&pwcmp=AIPREDMensile',
        },
        isDefaultWhenSingleOption: true,
        name: 'Premium',
        term: 'monthly',
      },
      [STRIPE_PRODUCT_IDS.GAZZETTA_PLUS_THREE_MONTHLY]: {
        externalUrl: {
          NON_PROD:
            'https://dev.abbonamenti.gazzetta.it/login/?OfferId=OFF-00137715_1&returnUrl=https://dv1-fpsm.gazzetta.it&pwcmp=AIPREDMondOdds',
          PROD: 'https://abbonamenti.gazzetta.it/login/?OfferId=OFF-00137715_1&returnUrl=https://aipredictor.gazzetta.it&pwcmp=AIPREDMondOdds',
        },
        isDefaultWhenSingleOption: false,
        name: 'Premium',
        term: 'monthly',
      },
    },
  },
} satisfies Record<SupportedRepubName, SubscriptionConfig>;
